import * as turf from "@turf/turf"
import { intersect, polygon, union } from "@turf/turf"
import { GeoJSONType } from "../types/geoJSON"
import { CoordinatesType, ShapeFeatureType } from "../types/shape"
import {
  leafletToTurfCoordinates,
  leafletToTurfPosition,
  turfToLeafletPosition,
} from "./shapeHelper"
import { getQuadrilateralPerimeterCornersFromAnyCoordinates } from "./quadrilateralHelper"

export const getCoordinatesFromGeoJSON = (
  geoJsonItem: GeoJSONType | null
): CoordinatesType => {
  let coordinates: any = []
  if (
    geoJsonItem &&
    geoJsonItem.geometry &&
    geoJsonItem.geometry.type &&
    geoJsonItem.geometry
  ) {
    switch (geoJsonItem.geometry.type as any) {
      case "MultiPolygon":
        coordinates = geoJsonItem.geometry.coordinates[0][0]
        break
      case "Polygon":
        coordinates = geoJsonItem.geometry.coordinates[0]
        break
      case "LineString":
        coordinates = geoJsonItem.geometry.coordinates
        break
      case "Point":
        coordinates = [geoJsonItem.geometry.coordinates]
        break
      case "multipolygon":
        coordinates = geoJsonItem.geometry.coordinates[0][0]
        break
      case "polygon":
        coordinates = geoJsonItem.geometry.coordinates[0]
        break
      case "linestring":
        coordinates = geoJsonItem.geometry.coordinates
        break
      case "point":
        coordinates = [geoJsonItem.geometry.coordinates]
        break
      default:
        console.error("geoJson item is not handled correctly", geoJsonItem)
        break
    }

    if (
      !coordinates.every(
        (e) => typeof e[0] === "number" && typeof e[1] === "number"
      )
    ) {
      coordinates = []
    }
  }
  return coordinates
}
export const convertGeoJsonItem = (
  geoJsonItem: ShapeFeatureType,
  conversionMethod
) => {
  if (
    geoJsonItem &&
    geoJsonItem.geometry &&
    geoJsonItem.geometry.type &&
    geoJsonItem.geometry.coordinates
  ) {
    switch (geoJsonItem.geometry.type) {
      case "MultiPolygon":
        geoJsonItem.geometry.coordinates = geoJsonItem.geometry.coordinates.map(
          (polygon) =>
            polygon.map((line) =>
              line.map((coords) => conversionMethod(coords))
            )
        )
        break
      case "Polygon":
        geoJsonItem.geometry.coordinates = geoJsonItem.geometry.coordinates.map(
          (line) => line.map((coords) => conversionMethod(coords))
        )
        break
      case "LineString":
        geoJsonItem.geometry.coordinates = geoJsonItem.geometry.coordinates.map(
          (coords) => conversionMethod(coords)
        )
        break
      case "Point":
        geoJsonItem.geometry.coordinates = conversionMethod(
          geoJsonItem.geometry.coordinates
        )
        break
      default:
        console.error("geoJson item is not handled correctly", geoJsonItem)
        break
    }
  }
  return geoJsonItem
}
export const leafletToTurfGeoJsonItem = (geoJsonItem) => {
  return convertGeoJsonItem(geoJsonItem, leafletToTurfPosition)
}
export const turfToLeafletInGeoJson = (geoJsonItem) => {
  return convertGeoJsonItem(geoJsonItem, turfToLeafletPosition)
}
export const isAMultiPolygon = (geoJson: GeoJSONType | null) => {
  return (
    geoJson &&
    geoJson.geometry &&
    (geoJson.geometry.type === "MultiPolygon" ||
      geoJson.geometry.type === "multipolygon") &&
    geoJson.geometry.coordinates.length > 1
  )
}
export const isAPolygon = (geoJson: GeoJSONType | null) => {
  return (
    geoJson &&
    geoJson.geometry &&
    geoJson.geometry &&
    (geoJson.geometry.type === "Polygon" ||
      geoJson.geometry.type === "polygon" ||
      ((geoJson.geometry.type === "MultiPolygon" ||
        geoJson.geometry.type === "multipolygon") &&
        geoJson.geometry.coordinates.length === 1))
  )
}
export const geoJSONCoordinatesCount = (geoJsonItem: GeoJSONType) =>
  getCoordinatesFromGeoJSON(geoJsonItem).length
export const isAQuadrilateralGeoJSON = (geoJsonItem: GeoJSONType): boolean => {
  return geoJSONCoordinatesCount(geoJsonItem) === 5
}

export const getIntersectionShapeFeatureBetweenCoordinates = (
  coords1: CoordinatesType,
  coords2: CoordinatesType
): ShapeFeatureType | null => {
  const turfCoords1 = leafletToTurfCoordinates(coords1)
  const turfCoords2 = leafletToTurfCoordinates(coords2)
  const p1 = polygon([turfCoords1])
  const p2 = polygon([turfCoords2])
  const intersection = intersect(p1, p2)
  return intersection && turfToLeafletInGeoJson(intersection)
}

export const getUnionFeatureBetweenCoordinates = (
  coords1: CoordinatesType,
  coords2: CoordinatesType
): ShapeFeatureType | null => {
  const turfCoords1 = leafletToTurfCoordinates(coords1)
  const turfCoords2 = leafletToTurfCoordinates(coords2)
  const p1 = polygon([turfCoords1])
  const p2 = polygon([turfCoords2])
  const result = union(p1, p2)
  return result && turfToLeafletInGeoJson(result)
}

export const getIntersectionShapeFeatureBetweenGeoJSON = (
  geoJson1: GeoJSONType,
  geoJson2: GeoJSONType
): ShapeFeatureType | null => {
  return getIntersectionShapeFeatureBetweenCoordinates(
    getCoordinatesFromGeoJSON(geoJson1),
    getCoordinatesFromGeoJSON(geoJson2)
  )
}

export const getUnionShapeFeatureBetweenGeoJSON = (
  geoJson1: GeoJSONType,
  geoJson2: GeoJSONType
): ShapeFeatureType | null => {
  return getUnionFeatureBetweenCoordinates(
    getCoordinatesFromGeoJSON(geoJson1),
    getCoordinatesFromGeoJSON(geoJson2)
  )
}

const MINIMUM_DISTANCE_IN_KM = 0.1

export const stackUpAllGeoJSONItems = (
  geoJSONItems: (GeoJSONType | null)[]
): GeoJSONType | null =>
  geoJSONItems.length > 0
    ? geoJSONItems.reduce(
        (acc: GeoJSONType | null, product: GeoJSONType | null) =>
          !!acc && !!product && isAPolygon(acc) && isAPolygon(product)
            ? stackUpBetweenGeoJSON(acc, product)
            : null,
        geoJSONItems[0]
      )
    : null

export const stackUpBetweenGeoJSON = (
  geoJson1: GeoJSONType,
  geoJson2: GeoJSONType
): ShapeFeatureType | null => {
  return !!getIntersectionShapeFeatureBetweenGeoJSON(geoJson1, geoJson2) ||
    getMinimumDistanceBetweenGeoJSON(geoJson1, geoJson2) <
      MINIMUM_DISTANCE_IN_KM
    ? getStackUpShapeFeatureBetweenGeoJSON(geoJson1, geoJson2)
    : null
}

const getMinimumDistanceBetweenGeoJSON = (
  geoJson1: GeoJSONType,
  geoJson2: GeoJSONType
) => {
  const {
    upLeft: upLeft1,
    bottomLeft: bottomLeft1,
    bottomRight: bottomRight1,
  } = getQuadrilateralPerimeterCornersFromAnyCoordinates(
    getCoordinatesFromGeoJSON(geoJson1)
  )
  const {
    upLeft: upLeft2,
    bottomLeft: bottomLeft2,
    bottomRight: bottomRight2,
  } = getQuadrilateralPerimeterCornersFromAnyCoordinates(
    getCoordinatesFromGeoJSON(geoJson2)
  )

  return upLeft1[1] > upLeft2[1]
    ? turf.pointToLineDistance(
        turf.point(upLeft2),
        turf.lineString([bottomLeft1, bottomRight1], { units: "kilometers" })
      )
    : turf.pointToLineDistance(
        turf.point(upLeft1),
        turf.lineString([bottomLeft2, bottomRight2], { units: "kilometers" })
      )
}

const getStackUpShapeFeatureBetweenGeoJSON = (
  geoJson1: GeoJSONType,
  geoJson2: GeoJSONType
) => {
  const {
    upLeft: upLeft1,
    upRight: upRight1,
    bottomLeft: bottomLeft1,
    bottomRight: bottomRight1,
  } = getQuadrilateralPerimeterCornersFromAnyCoordinates(
    getCoordinatesFromGeoJSON(geoJson1)
  )
  const {
    upLeft: upLeft2,
    upRight: upRight2,
    bottomLeft: bottomLeft2,
    bottomRight: bottomRight2,
  } = getQuadrilateralPerimeterCornersFromAnyCoordinates(
    getCoordinatesFromGeoJSON(geoJson2)
  )

  return upLeft1[1] > upLeft2[1]
    ? turf.polygon([[upLeft1, upRight1, bottomRight2, bottomLeft2, upLeft1]])
    : turf.polygon([[upLeft2, upRight2, bottomRight1, bottomLeft1, upLeft2]])
}
